import React from "react";
import {
  LayoutDashboard,
  BellRing,
  BookOpen,
  CreditCard,
  Navigation,
  BarChart3,
  Tag,
  Users,
  Store,
  Headphones,
  Check,
  Clock,
  Bike,
  MapPin,
  Plus,
  Star,
} from "lucide-react";
import { IMAGES } from "@/data/adPackage";

const ICONS = {
  LayoutDashboard,
  BellRing,
  BookOpen,
  CreditCard,
  Navigation,
  BarChart3,
  Tag,
  Users,
  Store,
  Headphones,
};

export function FeatureIcon({ name, className }) {
  const Ico = ICONS[name] || Store;
  return <Ico className={className} />;
}

function Dot() {
  return <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-400" />;
}

// ---- Restaurant Dashboard ----
export function DashboardMockup() {
  const orders = [
    { id: "#BG-2481", items: "2× Paneer Tikka, 1× Naan", status: "New" },
    { id: "#BG-2480", items: "1× Chicken Biryani", status: "Preparing" },
    { id: "#BG-2479", items: "1× Masala Dosa, 2× Filter Coffee", status: "Ready" },
  ];
  const statusColor = {
    New: "text-orange-400 bg-orange-500/15",
    Preparing: "text-amber-300 bg-amber-500/15",
    Ready: "text-emerald-400 bg-emerald-500/15",
  };
  return (
    <div data-testid="mockup-dashboard" className="w-full rounded-2xl bg-neutral-950 border border-white/10 overflow-hidden shadow-2xl">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-neutral-900/80">
        <div className="flex items-center gap-2">
          <LayoutDashboard className="w-4 h-4 text-orange-400" />
          <span className="font-display font-bold text-sm text-white">Restaurant Dashboard</span>
        </div>
        <span className="flex items-center gap-1.5 text-[11px] text-emerald-400">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Live
        </span>
      </div>
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          {[
            { k: "Live Orders", v: "3" },
            { k: "Menu Items", v: "48" },
            { k: "Open Offers", v: "2" },
          ].map((s) => (
            <div key={s.k} className="rounded-xl bg-neutral-900 border border-white/5 px-3 py-2">
              <div className="font-display text-xl font-black text-white">{s.v}</div>
              <div className="text-[10px] uppercase tracking-wide text-neutral-500">{s.k}</div>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          {orders.map((o) => (
            <div key={o.id} className="flex items-center justify-between rounded-xl bg-neutral-900 border border-white/5 px-3 py-2">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-white">{o.id}</div>
                <div className="text-[11px] text-neutral-400 truncate">{o.items}</div>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${statusColor[o.status]}`}>{o.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- Live Order card ----
export function LiveOrderMockup() {
  return (
    <div data-testid="mockup-live-order" className="w-full rounded-2xl bg-neutral-950 border border-orange-500/30 overflow-hidden shadow-2xl">
      <div className="flex items-center gap-2 px-4 py-3 bg-orange-500/15 border-b border-orange-500/20">
        <BellRing className="w-4 h-4 text-orange-400" />
        <span className="font-display font-bold text-sm text-white">New Order · #BG-2482</span>
      </div>
      <div className="p-4 space-y-2 text-sm">
        <div className="flex justify-between text-neutral-300"><span>1× Butter Chicken</span><span>₹320</span></div>
        <div className="flex justify-between text-neutral-300"><span>2× Butter Naan</span><span>₹120</span></div>
        <div className="flex justify-between text-neutral-300"><span>1× Gulab Jamun</span><span>₹90</span></div>
        <div className="flex gap-2 pt-2">
          <button className="flex-1 rounded-lg bg-emerald-500 text-black font-semibold py-2 text-xs">Accept</button>
          <button className="flex-1 rounded-lg bg-neutral-800 text-neutral-300 font-semibold py-2 text-xs">Decline</button>
        </div>
      </div>
    </div>
  );
}

// ---- Digital Menu ----
export function DigitalMenuMockup() {
  const items = [
    { n: "Paneer Butter Masala", p: "₹280", img: IMAGES.foodFlatlay, veg: true },
    { n: "Chicken Biryani", p: "₹320", img: IMAGES.kitchenPass, veg: false },
    { n: "Masala Dosa", p: "₹150", img: IMAGES.orderingHands, veg: true },
  ];
  return (
    <div data-testid="mockup-menu" className="w-full rounded-2xl bg-neutral-950 border border-white/10 overflow-hidden shadow-2xl">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-neutral-900/80">
        <BookOpen className="w-4 h-4 text-orange-400" />
        <span className="font-display font-bold text-sm text-white">Digital Menu</span>
      </div>
      <div className="p-3 space-y-2">
        {items.map((it) => (
          <div key={it.n} className="flex items-center gap-3 rounded-xl bg-neutral-900 border border-white/5 p-2">
            <img src={it.img} alt="" className="w-14 h-14 rounded-lg object-cover" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className={`w-3 h-3 border ${it.veg ? "border-emerald-500" : "border-red-500"} flex items-center justify-center rounded-[3px]`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${it.veg ? "bg-emerald-500" : "bg-red-500"}`} />
                </span>
                <span className="text-xs font-semibold text-white truncate">{it.n}</span>
              </div>
              <div className="flex items-center gap-1 text-[10px] text-amber-400 mt-0.5"><Star className="w-3 h-3 fill-amber-400" /> 4.5</div>
            </div>
            <div className="text-right">
              <div className="text-xs font-bold text-white">{it.p}</div>
              <button className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-orange-400 border border-orange-500/40 rounded-md px-2 py-0.5">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Checkout with transparent line items ----
export function CheckoutMockup() {
  return (
    <div data-testid="mockup-checkout" className="w-full rounded-2xl bg-neutral-950 border border-white/10 overflow-hidden shadow-2xl">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-neutral-900/80">
        <CreditCard className="w-4 h-4 text-orange-400" />
        <span className="font-display font-bold text-sm text-white">Checkout</span>
      </div>
      <div className="p-4 space-y-3">
        <div className="rounded-xl bg-neutral-900 border border-white/5 p-3 space-y-2 text-sm">
          <div className="flex justify-between text-neutral-300">
            <span>Food Cost</span>
            <span className="font-semibold text-white">₹530</span>
          </div>
          <div className="flex justify-between text-neutral-300">
            <span>Applicable Delivery Fee</span>
            <span className="font-semibold text-white">₹35</span>
          </div>
          <div className="h-px bg-white/10 my-1" />
          <div className="flex justify-between text-white">
            <span className="font-semibold">Total to Pay</span>
            <span className="font-display font-black text-orange-400">₹565</span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-neutral-400">
          <Dot /> Transparent pricing — Food Cost + applicable Delivery Fee
        </div>
        <button className="w-full rounded-xl brand-gradient text-white font-semibold py-2.5 text-sm">Pay Securely</button>
      </div>
    </div>
  );
}

// ---- Order Tracking ----
export function OrderTrackingMockup() {
  const steps = [
    { n: "Confirmed", icon: Check, done: true },
    { n: "Preparing", icon: Clock, done: true },
    { n: "On the Way", icon: Bike, done: false, active: true },
    { n: "Delivered", icon: MapPin, done: false },
  ];
  return (
    <div data-testid="mockup-tracking" className="w-full rounded-2xl bg-neutral-950 border border-white/10 overflow-hidden shadow-2xl">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-neutral-900/80">
        <Navigation className="w-4 h-4 text-orange-400" />
        <span className="font-display font-bold text-sm text-white">Order Tracking · #BG-2482</span>
      </div>
      <div className="p-4 space-y-3">
        {steps.map((s, i) => {
          const Ico = s.icon;
          return (
            <div key={s.n} className="flex items-center gap-3">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border ${
                  s.done
                    ? "bg-emerald-500/20 border-emerald-500 text-emerald-400"
                    : s.active
                    ? "brand-gradient border-transparent text-white"
                    : "bg-neutral-900 border-white/10 text-neutral-600"
                }`}
              >
                <Ico className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <div className={`text-xs font-semibold ${s.done || s.active ? "text-white" : "text-neutral-500"}`}>{s.n}</div>
                {s.active && <div className="text-[10px] text-orange-400">Arriving soon</div>}
              </div>
              {i < steps.length - 1 && <div className="w-8" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PhoneFrame({ children, className = "" }) {
  return (
    <div className={`relative mx-auto w-[220px] rounded-[2rem] border-[6px] border-neutral-800 bg-black p-2 shadow-2xl ${className}`}>
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-1.5 rounded-full bg-neutral-700" />
      <div className="mt-3 rounded-[1.4rem] overflow-hidden">{children}</div>
    </div>
  );
}
